// Lazy-loaded code-split chunks carry a content hash in their filename that changes on
// every deploy. A stale client — a bot crawling a cached page, or a real user on a tab
// opened before the deploy — requests a hash that no longer resolves, producing a
// ChunkLoadError. This is expected, self-healing (a reload fetches the new chunk), and
// unactionable from app code: the browser-RUM equivalent of the 502/503/504 deploy-window
// gateway noise dropped by is-relevant-browser-error.js. Match both the error class name
// and the webpack/rspack "Loading chunk <id> failed" message text (the notice may already
// be prefixed with "[JS] ").
export const CHUNK_LOAD_ERROR_PATTERN = /ChunkLoadError|Loading chunk\s+\S+\s+failed/i;

// Suppression is deliberately scoped to known NON-CRITICAL lazy-load call sites (deferred
// below-the-fold PDP enhancements). Gating on the call-site `source` tag keeps chunk-load
// failures on critical routes (checkout, payment) visible — only these enhancement loads
// are treated as expected deploy noise. Extend this set when adding a new non-critical
// dynamic import() whose failure is safe to ignore.
export const NON_CRITICAL_CHUNK_LOAD_SOURCES = new Set([
  "pdp_below_fold_init",
  "pdp_dtf_sheet_chunk_load"
]);

/**
 * True when a Honeybadger notice is an expected ChunkLoadError from a known non-critical
 * lazy-load call site, and therefore safe to drop. Pure and side-effect free so it can be
 * unit-tested and reused by the browser-global beforeNotify choke point in
 * honeybadger-loader.ts (which only has access to notice.message and notice.context).
 *
 * @param {unknown} message  notice.message (the chunk-load signature lives here)
 * @param {unknown} source   notice.context.source (the call-site tag)
 * @returns {boolean}
 */
export const isSuppressibleChunkLoadNotice = (message, source) =>
  typeof message === "string" &&
  CHUNK_LOAD_ERROR_PATTERN.test(message) &&
  typeof source === "string" &&
  NON_CRITICAL_CHUNK_LOAD_SOURCES.has(source);
